import React, { useEffect, useState } from "react";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { useAuth } from "../../context/AuthContext";
import { getUserNotifications, markNotificationAsRead, subscribeToNotifications } from "../../firebase/notificationService";
import { DataGrid } from "@mui/x-data-grid";
import { Button, CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Chip, Box } from "@mui/material";
import {
  Backup as BackupIcon,
  Restore as RestoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon
} from "@mui/icons-material";
import "./notifications.scss";

const Notifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    const unsubscribe = subscribeToNotifications(user.uid, (notifs) => {
      setNotifications(notifs);
      setLoading(false);
    });
    return () => unsubscribe && unsubscribe();
  }, [user]);

  const handleConfirmPayment = async (notif) => {
    setSnackbar({ open: true, message: 'Payment confirmed!', severity: 'success' });
    // In real app: update payment in DB, then notify tenant
  };

  const handleMarkAsRead = async (notifId) => {
    await markNotificationAsRead(notifId);
    setNotifications(notifications.map(n => n.id === notifId ? { ...n, read: true } : n));
  };

  const handleViewNotification = (notif) => {
    setSelectedNotification(notif);
    setViewModalOpen(true);
  };

  // Get icon for notification type
  const getNotificationIcon = (notification) => {
    const { type, data } = notification;
    
    if (type === 'backup') {
      const operationType = data?.operationType;
      switch (operationType) {
        case 'created':
        case 'completed':
          return <CheckCircleIcon color="success" />;
        case 'failed':
          return <ErrorIcon color="error" />;
        case 'deleted':
          return <DeleteIcon color="warning" />;
        case 'scheduled':
          return <ScheduleIcon color="info" />;
        default:
          return <BackupIcon color="primary" />;
      }
    }
    
    if (type === 'restore') {
      const operationType = data?.operationType;
      switch (operationType) {
        case 'completed':
          return <CheckCircleIcon color="success" />;
        case 'failed':
          return <ErrorIcon color="error" />;
        case 'dry_run':
          return <PreviewIcon color="info" />;
        case 'started':
          return <RestoreIcon color="warning" />;
        default:
          return <RestoreIcon color="primary" />;
      }
    }
    
    return null;
  };

  // Get status chip for notification
  const getStatusChip = (notification) => {
    const { type, data } = notification;
    
    if (type === 'backup') {
      const operationType = data?.operationType;
      switch (operationType) {
        case 'created':
        case 'completed':
          return <Chip label="Success" color="success" size="small" />;
        case 'failed':
          return <Chip label="Failed" color="error" size="small" />;
        case 'deleted':
          return <Chip label="Deleted" color="warning" size="small" />;
        case 'scheduled':
          return <Chip label="Scheduled" color="info" size="small" />;
        default:
          return <Chip label="Backup" color="primary" size="small" />;
      }
    }
    
    if (type === 'restore') {
      const operationType = data?.operationType;
      switch (operationType) {
        case 'completed':
          return <Chip label="Completed" color="success" size="small" />;
        case 'failed':
          return <Chip label="Failed" color="error" size="small" />;
        case 'dry_run':
          return <Chip label="Dry Run" color="info" size="small" />;
        case 'started':
          return <Chip label="In Progress" color="warning" size="small" />;
        default:
          return <Chip label="Restore" color="primary" size="small" />;
      }
    }
    
    return null;
  };

  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { 
      field: "type", 
      headerName: "Type", 
      width: 120, 
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          {getNotificationIcon(params.row)}
          {getStatusChip(params.row)}
        </Box>
      )
    },
    { field: "title", headerName: "Title", width: 200 },
    { field: "message", headerName: "Message", width: 220, renderCell: (params) => (
      <span style={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'block',
        maxWidth: 200
      }}>{params.value}</span>
    ) },
    { field: "createdAt", headerName: "Date", width: 180, valueGetter: (params) => {
      const val = params.row.createdAt;
      if (!val) return "N/A";
      if (val.seconds) return new Date(val.seconds * 1000).toLocaleString();
      return new Date(val).toLocaleString();
    } },
    { field: "status", headerName: "Status", width: 120, valueGetter: (params) => params.row.read ? "Read" : "Unread" },
    {
      field: "action",
      headerName: "Action",
      width: 220,
      renderCell: (params) => {
        const notif = params.row;
        return (
          <>
            <Button variant="outlined" size="small" style={{ marginRight: 8 }} onClick={() => handleViewNotification(notif)}>
              View
            </Button>
            <Button size="small" variant="outlined" onClick={() => handleMarkAsRead(notif.id)} disabled={notif.read}>
              Mark as Read
            </Button>
          </>
        );
      }
    }
  ];

  return (
    <div className="notifications">
      <Sidebar />
      <div className="notificationsContainer">
        <Navbar />
        <div className="datatable">
          <div className="datatableTitle">
            Notifications
          </div>
          {loading ? (
            <div className="loading-container">
              <CircularProgress />
              <p>Loading notifications...</p>
            </div>
          ) : (
            <DataGrid
              className="datagrid"
              rows={notifications}
              columns={columns}
              pageSize={9}
              rowsPerPageOptions={[9]}
              getRowId={row => row.id}
              disableSelectionOnClick
            />
          )}
        </div>
        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
        <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              {selectedNotification && getNotificationIcon(selectedNotification)}
              Notification Details
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedNotification && (
              <>
                <Typography variant="h6" gutterBottom>{selectedNotification.title}</Typography>
                <Typography variant="body1" gutterBottom>{selectedNotification.message}</Typography>
                
                {/* Show operation details for backup/restore notifications */}
                {selectedNotification.type === 'backup' && selectedNotification.data && (
                  <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
                    <Typography variant="subtitle2" gutterBottom>Backup Details:</Typography>
                    <Typography variant="body2">Backup Name: {selectedNotification.data.backupName}</Typography>
                    {selectedNotification.data.documentCount && (
                      <Typography variant="body2">Documents: {selectedNotification.data.documentCount}</Typography>
                    )}
                    {selectedNotification.data.operationType && (
                      <Typography variant="body2">Operation: {selectedNotification.data.operationType}</Typography>
                    )}
                    {selectedNotification.data.error && (
                      <Typography variant="body2" color="error">Error: {selectedNotification.data.error}</Typography>
                    )}
                  </Box>
                )}
                
                {selectedNotification.type === 'restore' && selectedNotification.data && (
                  <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
                    <Typography variant="subtitle2" gutterBottom>Restore Details:</Typography>
                    <Typography variant="body2">Backup Name: {selectedNotification.data.backupName}</Typography>
                    {selectedNotification.data.totalRestored && (
                      <Typography variant="body2">Documents Restored: {selectedNotification.data.totalRestored}</Typography>
                    )}
                    {selectedNotification.data.operationType && (
                      <Typography variant="body2">Operation: {selectedNotification.data.operationType}</Typography>
                    )}
                    {selectedNotification.data.error && (
                      <Typography variant="body2" color="error">Error: {selectedNotification.data.error}</Typography>
                    )}
                  </Box>
                )}
                
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {selectedNotification.createdAt && (typeof selectedNotification.createdAt === 'object' && selectedNotification.createdAt.seconds
                    ? new Date(selectedNotification.createdAt.seconds * 1000).toLocaleString()
                    : new Date(selectedNotification.createdAt).toLocaleString())}
                </Typography>
                <Typography variant="body2" color="textSecondary">Status: {selectedNotification.read ? 'Read' : 'Unread'}</Typography>
                
                {/* Show E-Wallet receipt image if present */}
                {selectedNotification.data && selectedNotification.data.receiptUrl && (
                  <div style={{ marginTop: 16 }}>
                    <Typography variant="body2">E-Wallet Receipt:</Typography>
                    <img
                      src={selectedNotification.data.receiptUrl}
                      alt="E-Wallet Receipt"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        marginTop: 8
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewModalOpen(false)} color="primary">Close</Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default Notifications; 